'use client';

import { IconHandClick } from '@tabler/icons-react';
import { ReleaseNoteSectionViewOf } from 'bff';
import { ReleaseNoteDemo } from 'database';
import { useTranslations } from 'i11n';
import { useCallback, useState } from 'react';
import { Button } from 'ui';
import { useReleaseNoteDemo } from '../ReleaseNoteDemosContext';

// Stands in for a demo where there is none to run, e.g. the backoffice preview.
const DemoPlaceholder = ({ demo }: { demo: ReleaseNoteDemo }) => {
  const t = useTranslations();

  return (
    <div className='flex flex-col items-center gap-2 px-6 py-10 text-center'>
      <IconHandClick className='h-6 w-6 text-primary' />
      <p className='text-sm font-semibold text-foreground'>
        {t(`release-notes.demo.names.${demo}`)}
      </p>
      <p className='text-xs text-muted-foreground'>
        {t('release-notes.demo.preview-only')}
      </p>
    </div>
  );
};

// The live piece of a note, framed as a sample so it is never mistaken for
// the real screen. Starting over remounts it with its sample data.
export const DemoSection = ({
  section,
}: {
  section: ReleaseNoteSectionViewOf<'demo'>;
}) => {
  const t = useTranslations();
  const Demo = useReleaseNoteDemo(section.content.demo);
  const [run, setRun] = useState(0);

  const restart = useCallback(() => setRun((current) => current + 1), []);

  return (
    <>
      <div className='overflow-hidden rounded border border-stone-200 bg-white'>
        <div className='flex flex-wrap items-center gap-x-2.5 gap-y-1 border-b border-stone-200 bg-stone-50 px-4 py-2 text-[0.8125rem] text-muted-foreground'>
          <span
            aria-hidden
            className='h-[7px] w-[7px] rounded-full bg-primary'
          />
          {t('release-notes.demo.badge')}
          <span className='flex-1' />
          <span className='hidden sm:inline'>
            {t('release-notes.demo.sample-data')}
          </span>
          {Demo && (
            <Button
              type='button'
              variant='ghost'
              size='sm'
              onClick={restart}
              className='h-7 px-2 text-[0.8125rem] font-semibold text-foreground'
            >
              {t('release-notes.demo.restart')}
            </Button>
          )}
        </div>
        <div className='bg-stone-50/40 p-2 md:p-4'>
          {Demo ? (
            <Demo key={run} sectionId={section.id} />
          ) : (
            <DemoPlaceholder demo={section.content.demo} />
          )}
        </div>
      </div>
      <p className='mt-2.5 text-[0.8125rem] leading-[1.45] text-stone-600'>
        {section.content.caption ?? t('release-notes.demo.sandbox')}{' '}
        {section.content.caption && (
          <span className='whitespace-nowrap text-xs text-muted-foreground'>
            {t('release-notes.demo.credit')}
          </span>
        )}
      </p>
    </>
  );
};
